import { useEffect, useState } from 'react';
import { navigate } from '../App';
import { Board } from '../game/Board';
import { PromptDialog, PropertySheet, ShopSheet } from '../game/Dialogs';
import { EvadeDice, MoneyFlyLayer, Moments, MstLayer, OrderDraw, TauntLayer, TensionScene } from '../game/Effects';
import { Hud, bubbleText } from '../game/Hud';
import { PhraseTray, Players } from '../game/Players';
import { decider, decision, globalEffects } from '../game/selectors';
import { useGameAudio } from '../game/useGameAudio';
import { audio } from '../lib/audio';
import { TENSION } from '../lib/content';
import { type Line, describe } from '../lib/describe';
import { prefs } from '../lib/storage';
import { End } from './End';
import { useRoomCtx } from './RoomScreen';

export function Game() {
  const ctx = useRoomCtx();
  const { room, you, bus, act } = ctx;
  const g = room.game!;
  const [sheet, setSheet] = useState<number | null>(null);
  const [phrases, setPhrases] = useState(false);
  const [menu, setMenu] = useState(false);
  const [shop, setShop] = useState(false);
  const [lines, setLines] = useState<(Line & { id: number })[]>([]);
  const [hot, setHot] = useState<number | null>(null);
  const [showEnd, setShowEnd] = useState(true);

  // Notícias: frases a partir dos eventos.
  useEffect(() => {
    let id = 0;
    return bus.onEvents(({ events }) => {
      const ctxD = { name: ctx.name, square: ctx.square, money: ctx.money, you };
      const fresh = events
        .map((e) => describe(e, ctxD))
        .filter((l): l is Line => l !== null)
        .map((l) => ({ ...l, id: ++id + Date.now() }));
      if (fresh.length) setLines((old) => [...fresh.reverse(), ...old].slice(0, 150));
      const moved = [...events].reverse().find((e) => e.type === 'moved');
      if (moved && moved.type === 'moved') {
        setHot(moved.to);
        setTimeout(() => setHot((h) => (h === moved.to ? null : h)), 1800);
      }
    });
  }, [bus, ctx.name, ctx.square, ctx.money, you]);

  const finished = room.status === 'finished';
  useGameAudio();

  // Escolha no tabuleiro (Volta ao Mundo e Copa).
  const d = decision(g);
  let pickable: Set<number> | null = null;
  let onPick: ((i: number) => void) | null = null;
  if (d.kind === 'prompt' && d.prompt.player === you) {
    const p = d.prompt;
    if (p.kind === 'tour') {
      pickable = new Set(p.options);
      onPick = (i) => act({ type: 'TOUR', to: i });
    } else if (p.kind === 'championship') {
      pickable = new Set(p.options);
      onPick = (i) => act({ type: 'HOST', square: i });
    }
  }
  const who = decider(d);
  const talk = who && who !== you ? bubbleText(d) : null;
  const fx = globalEffects(g);

  return (
    <div className={`game tension-${g.tension} ${prefs.reducedEffects() ? 'is-reduced' : ''}`}>
      <div className="sky" aria-hidden="true">
        <i className="sky-squares" />
        <i className="sky-siren" />
        <TensionScene level={g.tension} />
      </div>

      <main className="table">
        <Board
          onSquare={(i) => (pickable?.has(i) && onPick ? onPick(i) : setSheet(i))}
          pickable={pickable}
          highlight={hot}
          bubble={talk && who ? { player: who, text: talk } : null}
        >
          {(layout) => <Hud layout={layout} news={lines[0] ?? null} onShop={() => setShop(true)} />}
        </Board>
      </main>

      <Players onPhrases={() => setPhrases(true)} />

      <nav className="side-buttons" aria-label="Opções">
        <button className="round-btn is-shop" onClick={() => setShop(true)} aria-label="Lojinha de cartas">
          <span aria-hidden="true">🛒</span>
        </button>
        <button className="round-btn" onClick={() => setPhrases(true)} aria-label="Mandar uma frase">
          <span aria-hidden="true">💬</span>
        </button>
        <MuteButton />
        <button className="round-btn" onClick={() => setMenu(true)} aria-label="Configurações e volume">
          <svg viewBox="0 0 24 24" aria-hidden="true">
            <path
              fill="currentColor"
              d="M12 8.6a3.4 3.4 0 1 0 0 6.8 3.4 3.4 0 0 0 0-6.8Zm8.4 4.5-.1-2.2 2-1.6-2-3.5-2.4.9a8 8 0 0 0-1.9-1.1L15.6 3h-4.1l-.4 2.6c-.7.3-1.3.6-1.9 1.1l-2.4-.9-2 3.5 2 1.6a8 8 0 0 0 0 2.2l-2 1.6 2 3.5 2.4-.9c.6.5 1.2.8 1.9 1.1l.4 2.6h4.1l.4-2.6c.7-.3 1.3-.6 1.9-1.1l2.4.9 2-3.5-2-1.5Z"
            />
          </svg>
        </button>
      </nav>

      <div className="ticker" aria-live="polite">
        {fx.length > 0 && <span className="ticker-fx">{fx.map((e) => (e.kind === 'apagao' ? 'Apagão' : 'Inflação')).join(' + ')}</span>}
        {lines[0] && (
          <span key={lines[0].id} className={`ticker-line tone-${lines[0].tone}`}>
            {lines[0].text}
          </span>
        )}
      </div>

      <MoneyFlyLayer />
      <MstLayer />
      <EvadeDice />
      <Moments />
      <OrderDraw />
      <TauntLayer />
      {!finished && <PromptDialog />}
      {sheet !== null && <PropertySheet index={sheet} onClose={() => setSheet(null)} />}
      {phrases && <PhraseTray onClose={() => setPhrases(false)} />}
      {shop && <ShopSheet onClose={() => setShop(false)} />}
      {menu && <Menu lines={lines} onClose={() => setMenu(false)} />}
      {finished && showEnd && <End onClose={() => setShowEnd(false)} />}
      {finished && !showEnd && (
        <button className="pill pill-blue end-reopen" onClick={() => setShowEnd(true)}>
          Ver resultado
        </button>
      )}
    </div>
  );
}

function MuteButton() {
  const [muted, setMuted] = useState(prefs.muted());
  return (
    <button
      className="round-btn"
      onClick={() => {
        const next = !muted;
        prefs.setMuted(next);
        setMuted(next);
        audio.unlock();
        audio.applyPrefs();
      }}
      aria-label={muted ? 'Ligar o som' : 'Desligar o som'}
      aria-pressed={muted}
    >
      <span aria-hidden="true">{muted ? '🔇' : '🔊'}</span>
    </button>
  );
}

function Slider({ label, value, onChange }: { label: string; value: number; onChange: (v: number) => void }) {
  return (
    <label className="slider">
      <span className="slider-top">
        <span>{label}</span>
        <b>{Math.round(value * 100)}%</b>
      </span>
      <input type="range" min={0} max={100} step={5} value={Math.round(value * 100)} onChange={(e) => onChange(Number(e.target.value) / 100)} />
    </label>
  );
}

function Menu({ lines, onClose }: { lines: (Line & { id: number })[]; onClose: () => void }) {
  const { room } = useRoomCtx();
  const [tab, setTab] = useState<'config' | 'historico'>('config');
  const [reduced, setReduced] = useState(prefs.reducedEffects());
  const [muted, setMuted] = useState(prefs.mutedTaunts());
  const [vol, setVol] = useState(prefs.volume());
  const g = room.game!;
  return (
    <div className="tray-backdrop" onClick={onClose}>
      <div className="tray tray-menu" role="dialog" aria-label="Configurações" onClick={(e) => e.stopPropagation()}>
        <div className="tray-tabs" role="tablist">
          <button role="tab" aria-selected={tab === 'config'} className={tab === 'config' ? 'is-on' : ''} onClick={() => setTab('config')}>
            Configurações
          </button>
          <button role="tab" aria-selected={tab === 'historico'} className={tab === 'historico' ? 'is-on' : ''} onClick={() => setTab('historico')}>
            Histórico
          </button>
        </div>
        {tab === 'config' ? (
          <>
            <Slider
              label="Volume"
              value={vol}
              onChange={(v) => {
                setVol(v);
                prefs.setVolume(v);
                prefs.setMuted(false);
                audio.unlock();
                audio.applyPrefs();
                audio.sfx('coin');
              }}
            />
            <label className="toggle">
              <input
                type="checkbox"
                checked={reduced}
                onChange={(e) => {
                  prefs.setReducedEffects(e.target.checked);
                  setReduced(e.target.checked);
                }}
              />
              Efeitos reduzidos (sem tremer nem piscar)
            </label>
            <label className="toggle">
              <input
                type="checkbox"
                checked={muted}
                onChange={(e) => {
                  prefs.setMutedTaunts(e.target.checked);
                  setMuted(e.target.checked);
                }}
              />
              Esconder provocações entre os outros
            </label>
            <p className="tray-line">
              Mesa {room.code} · Rodada {g.round} · Tensão: <strong>{TENSION[g.tension].name}</strong>
            </p>
            <div className="tray-actions">
              <button className="pill pill-blue" onClick={onClose}>
                Voltar ao jogo
              </button>
              <button className="pill pill-gray" onClick={() => navigate('/')}>
                Sair (o bot joga por você)
              </button>
            </div>
          </>
        ) : lines.length ? (
          <ol className="log">
            {lines.map((l) => (
              <li key={l.id} className={`tone-${l.tone}`}>
                {l.text}
              </li>
            ))}
          </ol>
        ) : (
          <p className="empty">As jogadas aparecem aqui.</p>
        )}
      </div>
    </div>
  );
}
